import { type ThunkAction, type UnknownAction } from "@reduxjs/toolkit";

import type { RootState } from "@/app/store";
import { resetOtcAccess } from "@/modules/dashboard/crypto-swaps/hooks/useOtcAccess";
import { resetInternalApiKeyAccess } from "@/modules/dashboard/internal-api-keys/hooks/useInternalApiKeyAccess";
import { TokenStorageService } from "@/shared/services/tokenStorage.service";
import type { UserEntity } from "@/shared/types/user.entity";

import { authService } from "../services/authService";
import {
  setUser,
  setLoading,
  setInitialized,
  setRequires2FA,
  setError,
  logout,
  setMustChangePassword,
} from "./authSlice";
import { reset as resetRegister } from "./registerSlice";

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "string") return error;
  if (typeof error === "object" && error !== null) {
    const maybeAxiosError = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };

    return maybeAxiosError.response?.data?.message ?? maybeAxiosError.message ?? fallback;
  }
  return fallback;
};

export const thunkInitializeAuth =
  (): ThunkAction<void, RootState, unknown, UnknownAction> => async (dispatch) => {
    dispatch(setLoading({ isLoading: true }));
    try {
      const user = await authService.getMe();
      dispatch(setUser({ user }));
    } catch (_error) {
      dispatch(setUser({ user: null }));
    } finally {
      dispatch(setLoading({ isLoading: false }));
      dispatch(setInitialized());
    }
  };

export const thunkDoLogin =
  ({
    username,
    password,
    twoFactorCode,
    turnstileToken,
  }: {
    username: string;
    password: string;
    twoFactorCode?: string;
    turnstileToken?: string | null;
  }): ThunkAction<void, RootState, unknown, UnknownAction> =>
  async (dispatch) => {
    dispatch(setLoading({ isLoading: true }));
    try {
      const data = await authService.login(username, password, twoFactorCode, turnstileToken);

      if (data.access_token) {
        TokenStorageService.setToken(data.access_token);
      }

      if (data.refresh_token) {
        TokenStorageService.setRefreshToken(data.refresh_token);
      }

      if (data.requires2FA) {
        dispatch(setError({ error: undefined }));
        dispatch(setRequires2FA({ requires2FA: data.requires2FA }));
        return;
      }

      if (data.must_change_password) {
        dispatch(setError({ error: undefined }));
        // A user with 2FA gets here on the second answer, after the code: the 2FA step is done, or the
        // login page would ask for the code again once the password is changed.
        dispatch(setRequires2FA({ requires2FA: false }));
        dispatch(
          setMustChangePassword({
            mustChangePassword: data.must_change_password,
          }),
        );
        return;
      }

      if (data.user) {
        dispatch(setUser({ user: data.user }));
        dispatch(setError({ error: undefined }));

        if (typeof BroadcastChannel !== "undefined") {
          const channel = new BroadcastChannel("auth");
          channel.postMessage({
            type: "LOGIN",
            token: data.access_token,
          });
          channel.close();
        }
      }
    } catch (error: unknown) {
      dispatch(setError({ error: getErrorMessage(error, "Error de autenticación") }));
      dispatch(setUser({ user: undefined }));
    } finally {
      dispatch(setLoading({ isLoading: false }));
    }
  };

/**
 * Establishes the session from a successful invitation accept (`POST /auth/invitations/accept` → 201).
 * Mirrors the login post-processing exactly: persist tokens (localhost bearer flow — in production the 201
 * already set the httpOnly cookies), hydrate the user into the store and broadcast LOGIN so other tabs sync.
 * The accept flow never returns `requires2FA` / `must_change_password` (the user was just created).
 */
export const thunkEstablishInvitationSession =
  ({
    accessToken,
    refreshToken,
    user,
  }: {
    accessToken: string;
    refreshToken?: string;
    user: UserEntity;
  }): ThunkAction<void, RootState, unknown, UnknownAction> =>
  (dispatch) => {
    if (accessToken) {
      TokenStorageService.setToken(accessToken);
    }
    if (refreshToken) {
      TokenStorageService.setRefreshToken(refreshToken);
    }

    dispatch(setUser({ user }));
    dispatch(setError({ error: undefined }));

    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel("auth");
      channel.postMessage({ type: "LOGIN", token: accessToken });
      channel.close();
    }
  };

export const thunkChangePassword =
  ({
    currentPassword,
    newPassword,
  }: {
    currentPassword: string;
    newPassword: string;
  }): ThunkAction<void, RootState, unknown, UnknownAction> =>
  async (dispatch) => {
    dispatch(setLoading({ isLoading: true }));
    try {
      await authService.changePassword(currentPassword, newPassword);
      dispatch(setMustChangePassword({ mustChangePassword: false }));
      dispatch(setError({ error: undefined }));
      // Optionally, you might want to log the user out or fetch the user again
      // For now, we'll just assume they are logged in or need to log in again depending on the flow
      // If this flow happens *during* login (before full auth), we might need to re-trigger login or just let them login again.
      // Assuming this is a "force change password on login" flow, usually we'd want to auto-login or ask them to login again.
      // Let's assume we just clear the flag and let them proceed or login.
      // If they were not fully logged in (no token), they might need to login again.
    } catch (error: unknown) {
      dispatch(setError({ error: getErrorMessage(error, "Error changing password") }));
    } finally {
      dispatch(setLoading({ isLoading: false }));
    }
  };

export const thunkDoLogout = (): ThunkAction<void, RootState, unknown, UnknownAction> => async (dispatch) => {
  try {
    await authService.logout();
  } catch (_error) {
    // Ignorar errores
  } finally {
    // Clear tokens in localhost
    TokenStorageService.clearToken();
    TokenStorageService.clearRefreshToken();

    resetOtcAccess();
    resetInternalApiKeyAccess();
    dispatch(logout());
    // Wipe register/verify state so "edit email" or any logout starts fresh.
    dispatch(resetRegister());

    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel("auth");
      channel.postMessage({ type: "LOGOUT" });
      channel.close();
    }
  }
};
